import { useState } from 'react';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useAppContext } from '@/hooks/useAppContext';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Plus, Trash2, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';

export function RelaysApp() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { config, updateConfig } = useAppContext();
  const { mutate: publishRelayList } = useNostrPublish();
  const [newRelayUrl, setNewRelayUrl] = useState('');

  const relays = config.relayMetadata.relays;

  const { data: relayStatus } = useQuery({
    queryKey: ['relays', 'status', relays.map(r => r.url).join(',')],
    queryFn: async () => {
      const status = new Map<string, boolean>();

      await Promise.all(
        relays.map(async (r) => {
          try {
            // Use the proper per-relay API from Nostrify
            const relay = (nostr as {
              relay?: (url: string) => {
                query: (filters: { kinds: number[]; limit: number }[], opts: { signal: AbortSignal }) => Promise<unknown>;
              };
            }).relay?.(r.url);
            if (!relay) {
              status.set(r.url, false);
              return;
            }

            // Try a very lightweight query with short timeout
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 2500);

            try {
              await relay.query([{ kinds: [0], limit: 1 }], { signal: controller.signal });
              status.set(r.url, true);
            } catch {
              status.set(r.url, false);
            } finally {
              clearTimeout(timeout);
            }
          } catch {
            status.set(r.url, false);
          }
        })
      );

      return status;
    },
    refetchInterval: 4000,
    enabled: relays.length > 0,
  });

  const addRelay = () => {
    if (!newRelayUrl.trim()) return;
    let url = newRelayUrl.trim();
    if (!url.startsWith('wss://') && !url.startsWith('ws://')) {
      url = `wss://${url}`;
    }
    if (relays.some((r) => r.url === url)) return;

    updateConfig((current) => ({
      ...current,
      relayMetadata: {
        ...config.relayMetadata,
        relays: [...relays, { url, read: true, write: true }],
        updatedAt: Math.floor(Date.now() / 1000),
      },
    }));
    setNewRelayUrl('');
  };

  const removeRelay = (url: string) => {
    updateConfig((current) => ({
      ...current,
      relayMetadata: {
        ...config.relayMetadata,
        relays: relays.filter((r) => r.url !== url),
        updatedAt: Math.floor(Date.now() / 1000),
      },
    }));
  };

  const toggleRead = (url: string) => {
    updateConfig((current) => ({
      ...current,
      relayMetadata: {
        ...config.relayMetadata,
        relays: relays.map((r) =>
          r.url === url ? { ...r, read: !r.read } : r
        ),
        updatedAt: Math.floor(Date.now() / 1000),
      },
    }));
  };

  const toggleWrite = (url: string) => {
    updateConfig((current) => ({
      ...current,
      relayMetadata: {
        ...config.relayMetadata,
        relays: relays.map((r) =>
          r.url === url ? { ...r, write: !r.write } : r
        ),
        updatedAt: Math.floor(Date.now() / 1000),
      },
    }));
  };

  const publishToNostr = () => {
    if (!user) return;
    const tags = relays.flatMap((r) => {
      const tags: string[][] = [];
      if (r.read && r.write) tags.push(['r', r.url]);
      else if (r.read) tags.push(['r', r.url, 'read']);
      else if (r.write) tags.push(['r', r.url, 'write']);
      return tags;
    });

    publishRelayList({
      kind: 10002,
      content: '',
      tags,
    });
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-border/50 bg-muted/30 flex items-center justify-between">
        <h2 className="text-sm font-semibold">Relay Manager</h2>
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={publishToNostr} title="Publish relay list">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="p-3 space-y-2">
          {relays.map((relay) => {
            const isConnected = relayStatus?.get(relay.url) ?? false;
            return (
              <div
                key={relay.url}
                className="flex items-center gap-3 p-3 rounded-lg bg-card border border-border/50"
              >
                <div className={cn('w-2 h-2 rounded-full shrink-0', isConnected ? 'bg-green-500' : 'bg-red-500')} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{relay.url}</div>
                  <div className="flex items-center gap-3 mt-1">
                    <div className="flex items-center gap-1.5">
                      <Switch
                        checked={relay.read}
                        onCheckedChange={() => toggleRead(relay.url)}
                        className="scale-75 origin-left"
                      />
                      <span className="text-xs text-muted-foreground">Read</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Switch
                        checked={relay.write}
                        onCheckedChange={() => toggleWrite(relay.url)}
                        className="scale-75 origin-left"
                      />
                      <span className="text-xs text-muted-foreground">Write</span>
                    </div>
                  </div>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={() => removeRelay(relay.url)}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="p-3 border-t border-border/50">
        <div className="flex gap-2">
          <Input
            value={newRelayUrl}
            onChange={(e) => setNewRelayUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addRelay()}
            placeholder="wss://relay.example.com"
            className="h-9 text-sm"
          />
          <Button size="icon" className="h-9 w-9 shrink-0" onClick={addRelay}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
